import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useQuery } from '@gogame/query';

import { loadReleaseVersion } from '../source/release';
import { releaseVersion } from './queryKeyFactory';

import { registerRequestInterceptor } from 'api';
const INACTIVE_TAB_TIME = 5 * 60 * 1000;
const FIRST_FETCH_DELAY = 60 * 1000;
const REFETCH_INTERVAL = 2 * 120 * 1000;
const REACT_QUERY_STALE_TIME = 10 * 1000; // frequency no more than one request on 10 seconds
const REACT_QUERY_CACHE_TIME = 10 * 1000;
const RUN_TIME = Number(new Date());

export const useLoadReleaseVersion = ({ enabled }) => {
  const [shouldFetch, setShouldFetch] = useState(false);

  const lastActivityTimeRef = useRef(RUN_TIME);

  let refetchInterval = useMemo(() => {
    if (!shouldFetch) {
      return false;
    }
    if (shouldFetch) {
      let isInactiveTab =
        Number(new Date()) - lastActivityTimeRef.current > INACTIVE_TAB_TIME;

      if (isInactiveTab) {
        return false;
      }

      return REFETCH_INTERVAL;
    }
  }, [shouldFetch]);

  const query = useQuery(
    releaseVersion,
    () => {
      let currentTime = Number(new Date());
      if (currentTime - lastActivityTimeRef.current > INACTIVE_TAB_TIME) {
        return Promise.reject('Inactive tab');
      }
      return loadReleaseVersion();
    },
    {
      staleTime: REACT_QUERY_STALE_TIME, // frequency no more than one request on 10 seconds
      cacheTime: REACT_QUERY_CACHE_TIME,
      refetchInterval: refetchInterval,
      refetchIntervalInBackground: false, //not allow fetch if tab not active
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      keepPreviousData: true,
      enabled: enabled && shouldFetch,
    }
  );

  const handleForceRefetch = useCallback(() => {
    lastActivityTimeRef.current = Number(new Date());
    return query.refetch;
  }, [query]);

  const requestInterceptorCallback = useCallback((request) => {
    let newTime = Number(new Date());

    if (request.url !== '/release.json') {
      const refetchCondition =
        newTime - lastActivityTimeRef.current > INACTIVE_TAB_TIME;
      if (newTime > lastActivityTimeRef.current) {
        lastActivityTimeRef.current = newTime;
      }
      if (refetchCondition) {
        query.refetch();
      }
    }

    return request;
  }, []);

  useEffect(() => {
    if (enabled) {
      return registerRequestInterceptor(
        requestInterceptorCallback,
        'release-check'
      );
    }
  }, [enabled, requestInterceptorCallback]);

  useEffect(() => {
    if (enabled) {
      const timer = setTimeout(() => {
        setShouldFetch(true);
      }, FIRST_FETCH_DELAY);

      return () => clearTimeout(timer);
    }
  }, [enabled]);

  return {
    ...query,
    refetch: handleForceRefetch,
  };
};
